import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useMemberId } from '@trello/authentication';
import { useBoardMembers } from '@trello/business-logic-react/board';
import { useDynamicBoardTokens } from '@trello/dynamic-tokens/dynamic-board-tokens';
import { useBoardHasPremiumFeature } from '@trello/entitlements';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useBoardId, useWorkspaceId } from '@trello/id-context';
import { Key, Scope, useShortcut } from '@trello/keybindings';
import {
  getLocation,
  isActiveRoute,
  RouteId,
  routerState,
} from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { useSharedState, useSharedStateSelector } from '@trello/shared-state';
import {
  useAreMultiplePanelsOpen,
  useIsBoardPanelOpen,
} from '@trello/split-screen';
import type { BoardMenuTestIds, BoardTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { useLazyComponent } from '@trello/use-lazy-component';

import { getBoardUrl } from 'app/scripts/controller/urls';
import { LabelState } from 'app/scripts/view-models/LabelState';
import { BoardBanners } from 'app/src/components/BoardBanners';
import { SingleBoardCalendarView } from 'app/src/components/BoardCalendarView';
import { LazyBoardHeader } from 'app/src/components/BoardHeader';
import { useIsBoardHeaderAndMenuRedesignEnabled } from 'app/src/components/BoardHeader/useIsBoardHeaderAndMenuRedesignEnabled';
import { BoardLimitsWarnings } from 'app/src/components/BoardLimitsWarnings';
import { LazyBoardListView } from 'app/src/components/BoardListView';
import { LazyBoardMenuSidebar } from 'app/src/components/BoardMenu';
import { LazyTeamOnboardingPupPopover } from 'app/src/components/BusinessClassTeamOnboardingPupPopover';
import { openCardBack } from 'app/src/components/CardFront/openCardBack';
import { useIsKeyboardShortcutsEnabled } from 'app/src/components/Shortcuts/useIsKeyboardShortcutsEnabled';
import { SingleBoardTableView } from 'app/src/components/TableView/SingleBoardTableView';
import { TimelineViewWrapper } from 'app/src/components/TimelineViewWrapper';
import { BoardPowerUpsView } from './BoardPowerUpsView';
import { boardSidebarState } from './boardSidebarState';
import { BoardViewBoundary } from './BoardViewBoundary';
import { legacyBoardModelsSharedState } from './legacyBoardModelsSharedState';
import { useBoardFiltersOnBoardView } from './useBoardFiltersOnBoardView';
import { boardPageState } from './useBoardPageState';
import { useReadBoardNotifications } from './useReadBoardNotifications';

import styles from './BoardView.less';

function BoardViewSwitcher({
  boardId,
  isCalendarPowerUpEnabled,
}: {
  boardId: string;
  isCalendarPowerUpEnabled: boolean;
}) {
  const { secondaryView, primaryView } = useSharedStateSelector(
    boardPageState,
    useCallback(
      (state) => ({
        secondaryView: state.secondaryViewParams?.view,
        primaryView: state.primaryViewParams?.view,
      }),
      [],
    ),
  );

  const { value: useCalendarViewInPlaceOfPup } = useFeatureGate(
    'trello_xf_use_view_for_calendar_powerup',
  );

  const BoardDashboardView = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "board-dashboard-view" */ 'app/src/components/BoardDashboardView'
      ),
    { preload: false, namedImport: 'BoardDashboardView' },
  );

  const BoardButlerView = useLazyComponent(
    () =>
      import(/* webpackChunkName: "board-butler-view" */ './BoardButlerView'),
    { preload: false, namedImport: 'BoardButlerView' },
  );

  const LegacyCalendarPowerUpView = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "legacy-calendar-view" */ './LegacyCalendarPowerUpView'
      ),
    { preload: false, namedImport: 'LegacyCalendarPowerUpView' },
  );

  const hasViewsFeature = useBoardHasPremiumFeature(boardId, 'views');

  const MapView = useLazyComponent(
    () =>
      import(/* webpackChunkName: "map-view" */ 'app/src/components/MapView'),
    { preload: false, namedImport: 'MapView' },
  );

  switch (secondaryView) {
    case 'power-up':
    case 'power-ups':
      return (
        <BoardViewBoundary feature="Board Power Ups">
          <BoardPowerUpsView />
        </BoardViewBoundary>
      );
    case 'butler': {
      return (
        <BoardViewBoundary feature="Board Butler">
          <BoardButlerView boardId={boardId} />
        </BoardViewBoundary>
      );
    }
    case 'calendar': {
      if (isCalendarPowerUpEnabled) {
        return (
          <BoardViewBoundary feature="Board Calendar">
            {useCalendarViewInPlaceOfPup ? (
              <SingleBoardCalendarView
                idBoard={boardId}
                navigateToCard={(id) => {
                  openCardBack(id);
                }}
                showCloseButton={true}
              />
            ) : (
              <LegacyCalendarPowerUpView boardId={boardId} />
            )}
          </BoardViewBoundary>
        );
      }
      break;
    }
    default:
      break;
  }

  if (!hasViewsFeature) {
    return <LazyBoardListView />;
  }

  switch (primaryView) {
    case 'calendar-view':
      return (
        <BoardViewBoundary feature="Board Calendar View">
          <SingleBoardCalendarView
            idBoard={boardId}
            navigateToCard={(id) => {
              openCardBack(id);
            }}
          />
        </BoardViewBoundary>
      );
    case 'table':
      return (
        <BoardViewBoundary feature="Board Table">
          <SingleBoardTableView
            idBoard={boardId}
            navigateToCard={(id) => {
              openCardBack(id);
            }}
          />
        </BoardViewBoundary>
      );
    case 'timeline':
      return (
        <BoardViewBoundary feature="Board Timeline">
          <TimelineViewWrapper
            idBoard={boardId}
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            navigateToCard={(id: any, params: any) => {
              openCardBack(id);
            }}
          />
        </BoardViewBoundary>
      );
    case 'dashboard':
      // TODO: Implement loading and error handling states for Dashboard view.
      return (
        <BoardViewBoundary feature="Board Dashboard">
          <BoardDashboardView
            navigateToBoardView={() => {
              const boardUrl = getBoardUrl(boardId);
              navigate(boardUrl, { trigger: true });
            }}
          />
        </BoardViewBoundary>
      );
    case 'map':
      return (
        <BoardViewBoundary feature="Board Map">
          <MapView />
        </BoardViewBoundary>
      );
    case 'board':
    default:
      return <LazyBoardListView />;
  }
}

export const BoardView: FunctionComponent = () => {
  const workspaceId = useWorkspaceId();
  const boardId = useBoardId();
  const isKeyboardShortcutsEnabled = useIsKeyboardShortcutsEnabled();
  const board = useSharedStateSelector(
    legacyBoardModelsSharedState,
    useCallback((state) => state.board.model, []),
  );
  const boardViewRef = useRef<HTMLDivElement | null>(null);

  const {
    getMemberPermissionLevel,
    getMemberType,
    loading: loadingMembers,
  } = useBoardMembers(boardId);
  const memberId = useMemberId();

  const isInviteAcceptRoute = useSharedStateSelector(
    routerState,
    useCallback(
      (state) => isActiveRoute(state, RouteId.INVITE_ACCEPT_BOARD),
      [],
    ),
  );

  const isBoardPanelOpen = useIsBoardPanelOpen();
  const areMultiplePanelsOpen = useAreMultiplePanelsOpen();

  const { headerRedesignEnabled, menuRedesignEnabled } =
    useIsBoardHeaderAndMenuRedesignEnabled();

  useEffect(() => {
    const permLevel = getMemberPermissionLevel(memberId);
    if (
      !loadingMembers &&
      permLevel === 'none' &&
      // getMemberType returns undefined for ghosts, do not redirect ghosts
      getMemberType(memberId) !== undefined &&
      !isInviteAcceptRoute
    ) {
      navigate('/', { trigger: true });
    }
  }, [
    getMemberPermissionLevel,
    loadingMembers,
    memberId,
    isInviteAcceptRoute,
    getMemberType,
  ]);

  const [isShowingSidebar, setIsShowingSidebar] =
    useSharedState(boardSidebarState);

  const toggleSidebar = useCallback(() => {
    setIsShowingSidebar(!isShowingSidebar);
  }, [isShowingSidebar, setIsShowingSidebar]);

  useShortcut(
    (event) => {
      // const wasShowingLabelText = LabelState.getShowText();
      LabelState.toggleText();
      event.preventDefault();
      // const toggleValue = wasShowingLabelText ? 'hide' : 'show';
      // TODO: Implement analytics
      // sendShortcutEvent('toggleLabelTextShortcut', { toggleValue });
    },
    {
      scope: Scope.Board,
      key: Key.SemiColon,
      enabled: isKeyboardShortcutsEnabled,
    },
  );

  useDynamicBoardTokens({
    ref: boardViewRef,
    dynamicTokens: [
      'dynamic.button',
      'dynamic.star',
      'dynamic.text',
      'dynamic.background',
    ],
  });

  useReadBoardNotifications({ boardId });
  useBoardFiltersOnBoardView();

  const navigateToBoardPowerUps = useCallback(() => {
    navigate(getBoardUrl(boardId, 'power-ups'), { trigger: true });
  }, [boardId]);

  const shouldRenderTeamOnboardingPowerUpPopover = useCallback(() => {
    const { search } = getLocation();
    const searchParams = new URLSearchParams(search);
    return searchParams.get('cameFromGettingStarted') === 'true';
  }, []);

  const boardCanvasRef = useRef<HTMLDivElement>(null);

  /**
   * PERFORMANCE OPTIMIZATION:
   *
   * The board view can be rendered for a board route or a card route.
   * If the user navigates directly to a card route, we want to defer the render
   * path for the board canvas in order to priorize the card back ASAP.
   *
   * If the board canvas has ever rendered because the user has navigated to a
   * board route, the board canvas element ref should still be connected, so we
   * will keep it live.
   *
   * This should also automatically handle navigations from board to board
   * (e.g. navigating directly to a card on another board).
   */
  const shouldRenderBoardCanvas = useSharedStateSelector(
    routerState,
    useCallback(
      (state) =>
        boardCanvasRef.current?.isConnected ||
        isActiveRoute(state, RouteId.BOARD) ||
        isActiveRoute(state, RouteId.INVITE_ACCEPT_BOARD),
      [],
    ),
  );

  // This is really just a safe check for TS, but it might also be possible that
  // the board model gets set to null before this component has unmounted. Therefore,
  // to be safe we just return null here.
  if (!board) {
    return null;
  }

  return (
    <div
      ref={boardViewRef}
      className={cx('board-wrapper', {
        'is-show-menu': isShowingSidebar && !menuRedesignEnabled,
      })}
      data-testid={getTestId<BoardTestIds>('board-view')}
    >
      <div
        className={cx('board-main-content', {
          [styles.boardPanelPadding]: isBoardPanelOpen,
          [styles.multiplePanelPadding]: areMultiplePanelsOpen,
        })}
      >
        <BoardBanners />
        <div
          className={cx(
            'board-header',
            'u-clearfix',
            // Unfortunately this selector is used in the BoardName component to resize
            // the input. Ideally this should be removed.
            'js-board-header',
            headerRedesignEnabled && 'board-header-redesign-blur',
          )}
        >
          <LazyBoardHeader
            legacyBoardModel={board}
            showSidebar={toggleSidebar}
            isSidebarVisible={isShowingSidebar && !menuRedesignEnabled}
          />
        </div>
        <BoardLimitsWarnings />

        {shouldRenderBoardCanvas && (
          <div
            className="board-canvas"
            ref={boardCanvasRef}
            data-testid={getTestId<BoardTestIds>('board-canvas')}
          >
            <BoardViewSwitcher
              boardId={boardId}
              isCalendarPowerUpEnabled={board.isPowerUpEnabled('calendar')}
            />
          </div>
        )}

        {workspaceId && shouldRenderTeamOnboardingPowerUpPopover() && (
          <LazyTeamOnboardingPupPopover
            workspaceId={workspaceId}
            boardId={boardId}
            onShowPowerUps={navigateToBoardPowerUps}
          />
        )}
      </div>
      {/* .js-fill-board-menu */}
      {!menuRedesignEnabled && (
        <div
          className="board-menu"
          data-testid={getTestId<BoardMenuTestIds>('board-menu-container')}
        >
          <LazyBoardMenuSidebar />
        </div>
      )}
    </div>
  );
};
